import Intercom, { show, showNewMessage, shutdown, update } from '@intercom/messenger-js-sdk';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { userStore } from '@stores/User';
import { decodeJwt } from 'jose';

const _INTERCOM_APP_ID = 'ttqiwd1r';
const _INTERCOM_USER_JWT_KEY = 'intercom_user_jwt';

export class IntercomStore extends ReactionManager {
  _intercomToken: string | null = null;
  _intercomExpiry: number | null = null;
  _isInitialized: boolean = false;
  private _retryAbort: AbortController | null = null;

  constructor() {
    super();

    const intercomToken = localStorage.getItem(_INTERCOM_USER_JWT_KEY);
    if (intercomToken) {
      this._intercomToken = intercomToken;
      const decoded = decodeJwt(this._intercomToken);
      this._intercomExpiry = decoded.exp ?? null;
    }

    this.reaction(
      () => userStore.data,
      async (userData) => {
        if (userData && !this._isInitialized) {
          this._isInitialized = true;
          const abort = new AbortController();
          this._retryAbort = abort;

          const RETRY_DELAYS_MS = [60_000, 120_000, 240_000];
          let tokenAcquired = false;

          for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            if (abort.signal.aborted) break;

            if (attempt > 0) {
              console.log(
                `Intercom: retrying token generation (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length + 1})`,
              );
            }

            try {
              await this.checkToken();
              tokenAcquired = true;
              break;
            } catch (e) {
              if (attempt < RETRY_DELAYS_MS.length) {
                const delaySec = RETRY_DELAYS_MS[attempt] / 1000;
                console.warn(`Intercom token generation failed, retrying in ${delaySec}s:`, e);
                await this.abortableDelay(RETRY_DELAYS_MS[attempt], abort);
              } else {
                console.warn('Intercom token generation failed after all retries:', e);
              }
            }
          }

          if (this._retryAbort === abort) {
            this._retryAbort = null;
          }

          if (abort.signal.aborted) {
            return;
          }
          if (!tokenAcquired) {
            this._isInitialized = false;
            return;
          }

          Intercom({
            app_id: _INTERCOM_APP_ID,
            intercom_user_jwt: this._intercomToken,
            user_id: userData._id,
            email: userData.email,
            created_at: userData.createdAt,
          });
          update({
            theme_mode: 'dark',
          });
        } else if (!userData && this._isInitialized) {
          this.resetIntercom();
        }
      },
      { fireImmediately: true },
    );
  }

  private abortableDelay(ms: number, abort: AbortController): Promise<void> {
    if (abort.signal.aborted) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      abort.signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    });
  }

  private resetIntercom() {
    this._retryAbort?.abort();
    this._retryAbort = null;
    this._isInitialized = false;
    this._intercomToken = null;
    this._intercomExpiry = null;
    localStorage.removeItem(_INTERCOM_USER_JWT_KEY);
    shutdown();
  }

  shutdown() {
    this._retryAbort?.abort();
    shutdown();
  }

  private async checkToken() {
    if (this._intercomExpiry === null || this._intercomExpiry < Date.now() / 1000) {
      localStorage.removeItem(_INTERCOM_USER_JWT_KEY);
      await this.generateToken();
    }
  }

  private async generateToken() {
    const { intercomToken } = await userStore.apiWithAuth<{ intercomToken: string }>(
      '/user/intercom',
      {
        method: 'POST',
      },
    );
    localStorage.setItem(_INTERCOM_USER_JWT_KEY, intercomToken);
    this._intercomToken = intercomToken;
    const decoded = decodeJwt(this._intercomToken);
    this._intercomExpiry = decoded.exp ?? null;
  }
}

export const { Provider: IntercomProvider, useStore: useIntercom } = createContext(IntercomStore, {
  dispose: (store) => store.shutdown(),
});

/**
 * Opens the Intercom messenger from anywhere (components, stores, toast
 * templates) without needing the store/hook. With a message, opens the composer
 * prefilled; otherwise just shows the messenger. No-op until Intercom has
 * booted (logged-in users only), so it's safe to call unconditionally.
 */
export function openIntercomMessenger(prefilledMessage?: string) {
  if (prefilledMessage) {
    showNewMessage(prefilledMessage);
  } else {
    show();
  }
}
